<template>
  <b-overlay :show="isLoading">
    <div v-if="game">
      <b-card no-body class="mb-1">
        <b-card-body>
          <div class="d-flex flex-wrap align-items-center justify-content-between">
            <div>
              <h4 class="mb-25">
                <b-link :href="game.task_url" target="_blank">{{ game.name }}</b-link>
              </h4>
              <div class="text-muted">
                Сезон {{ game.season }}
                <span v-if="game.group_letter"> · Группа {{ game.group_letter }}</span>
                <span v-if="game.tour"> · Тур {{ game.tour }}</span>
                <span v-if="game.game_number"> · Игра {{ game.game_number }}</span>
              </div>
            </div>
            <div class="text-right">
              <div v-if="game.scheduled_at">
                <b-badge variant="light-success">{{ formatMskDateTime(game.scheduled_at) }}</b-badge>
                <b-badge v-if="game.host_name" variant="light-info" class="ml-25">{{ game.host_name }}</b-badge>
              </div>
              <b-badge v-else variant="light-warning">Дата подбирается</b-badge>
              <div class="mt-25">
                <b-link v-if="game.chat_invite_link" :href="game.chat_invite_link" target="_blank">ТГ-чат</b-link>
              </div>
            </div>
          </div>

          <!-- Ссылка страницы записи: игроки отмечают на ней подходящие слоты -->
          <!-- flex-wrap + mb-50: на мобилке кнопки переносятся под ссылку, а не уезжают вправо -->
          <div v-if="signupUrl" class="mt-1 d-flex flex-wrap align-items-center">
            <!-- Ссылка открывает страницу в режиме просмотра для админа (все отметки, без выбора),
                 а копируется обычная — та, что уходит в чат игрокам -->
            <b-link
              :href="adminSignupUrl || signupUrl"
              target="_blank"
              rel="noopener"
              class="mr-50 mb-50 text-break"
              title="Открыть страницу записи в режиме просмотра"
            >
              <code>{{ signupUrl }}</code>
            </b-link>
            <b-button
              v-b-tooltip.hover
              size="sm"
              variant="flat-primary"
              class="btn-icon mr-50 mb-50"
              title="Скопировать ссылку для игроков"
              @click="copySignupUrl"
            >
              <feather-icon icon="CopyIcon" />
            </b-button>
            <b-button
              v-if="game.chat_id && $can('manage', 'fnf-league')"
              size="sm"
              variant="outline-primary"
              class="mr-50 mb-50"
              :disabled="isSendingSignupLink"
              @click="sendSignupLink"
            >
              <b-spinner v-if="isSendingSignupLink" small class="mr-25" />
              <feather-icon v-else icon="SendIcon" class="mr-25" />
              Отправить в чат
            </b-button>
            <b-button
              v-if="game.chat_id && $can('manage', 'fnf-league')"
              size="sm"
              variant="outline-primary"
              class="mr-50 mb-50"
              :disabled="isSendingSlotsAdded"
              @click="sendSlotsAdded"
            >
              <b-spinner v-if="isSendingSlotsAdded" small class="mr-25" />
              <feather-icon v-else icon="PlusSquareIcon" class="mr-25" />
              Слоты добавлены
            </b-button>
            <b-button
              v-if="$can('manage', 'fnf-league')"
              size="sm"
              variant="outline-primary"
              class="mr-50 mb-50"
              :disabled="isSendingSignupLinksDm"
              @click="sendSignupLinksDm"
            >
              <b-spinner v-if="isSendingSignupLinksDm" small class="mr-25" />
              <feather-icon v-else icon="MessageCircleIcon" class="mr-25" />
              Отправить в ЛС
            </b-button>
          </div>
          <div v-else class="mt-1 text-muted">
            Код записи появится после синка из ПФ
          </div>
        </b-card-body>
      </b-card>

      <b-card v-if="locked" no-body class="mb-1">
        <b-card-body>
          <b-badge variant="light-success">Игра назначена</b-badge>
          <span class="ml-50">Отметки игроков закрыты.</span>
        </b-card-body>
      </b-card>

      <b-card no-body>
        <b-card-header><h4 class="mb-0">Слоты и отметки игроков</h4></b-card-header>
        <b-card-body>
          <div v-if="!slots.length" class="text-muted">Будущих слотов нет.</div>
          <b-table-simple v-else responsive class="mb-0">
            <b-thead>
              <b-tr>
                <b-th>Слот</b-th>
                <b-th v-for="player in game.players" :key="player.planfix_contact_id">
                  {{ player.name }}
                  <!-- Персональная ссылка записи в ЛС только этому игроку -->
                  <div v-if="signupUrl && $can('manage', 'fnf-league')" class="mt-25">
                    <b-button
                      size="sm"
                      variant="outline-primary"
                      class="text-nowrap"
                      :disabled="sendingDmContactId !== null || isSendingSignupLinksDm"
                      @click="sendSignupLinkDmToPlayer(player)"
                    >
                      <b-spinner v-if="sendingDmContactId === player.planfix_contact_id" small class="mr-25" />
                      <feather-icon v-else icon="MessageCircleIcon" class="mr-25" />
                      Отправить в ЛС
                    </b-button>
                  </div>
                </b-th>
                <b-th />
              </b-tr>
            </b-thead>
            <b-tbody>
              <!-- Зелёная строка: слот подходит всем игрокам -->
              <b-tr v-for="slot in slots" :key="slot.id" :variant="slot.all_yes ? 'success' : undefined">
                <b-td class="text-nowrap">
                  {{ formatMskDateTime(slot.starts_at) }} · {{ slot.host_name }}
                  <b-badge v-if="slot.occupied" variant="light-secondary" class="ml-25">занят</b-badge>
                  <b-badge v-if="slot.assigned" variant="light-success" class="ml-25">назначен</b-badge>
                </b-td>
                <b-td v-for="player in game.players" :key="player.planfix_contact_id" class="text-center">
                  <span v-if="slot.marks[player.planfix_contact_id] === 'yes'" class="text-success">✓</span>
                  <span v-else-if="slot.marks[player.planfix_contact_id] === 'no'" class="text-danger">✕</span>
                  <span v-else class="text-muted">—</span>
                </b-td>
                <b-td class="text-right">
                  <b-button
                    v-if="slot.all_yes && !slot.occupied && !locked && !slot.assigned && $can('manage', 'fnf-league')"
                    size="sm"
                    variant="success"
                    :disabled="assigningSlotId !== null"
                    @click="assignSlot(slot)"
                  >
                    <b-spinner v-if="assigningSlotId === slot.id" small />
                    <span v-else>Назначить</span>
                  </b-button>
                </b-td>
              </b-tr>
            </b-tbody>
          </b-table-simple>
        </b-card-body>
      </b-card>

      <b-card v-if="game.results.length" no-body>
        <b-card-header><h4 class="mb-0">Результаты</h4></b-card-header>
        <b-card-body>
          <div v-for="(result, index) in game.results" :key="index" :class="{ 'text-muted': result.did_not_play }">
            {{ result.place }}. {{ result.name }} — {{ result.did_not_play ? 'не играл' : result.points }}
            <span v-if="result.balls !== null" class="text-muted">({{ result.balls }})</span>
          </div>
        </b-card-body>
      </b-card>
    </div>
  </b-overlay>
</template>

<script>
import {
  BBadge, BButton, BCard, BCardBody, BCardHeader, BLink, BOverlay, BSpinner,
  BTableSimple, BTbody, BTd, BTh, BThead, BTr, VBTooltip,
} from 'bootstrap-vue'
import { ref, onUnmounted } from '@vue/composition-api'
import store from '@/store'
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import fnfLeagueStoreModule from './fnfLeagueStoreModule'

const STORE_MODULE_NAME = 'app-fnf-league'

export default {
  components: {
    BBadge, BButton, BCard, BCardBody, BCardHeader, BLink, BOverlay, BSpinner,
    BTableSimple, BTbody, BTd, BTh, BThead, BTr,
  },
  directives: {
    'b-tooltip': VBTooltip,
  },
  setup(_, { root }) {
    if (!store.hasModule(STORE_MODULE_NAME)) store.registerModule(STORE_MODULE_NAME, fnfLeagueStoreModule)
    onUnmounted(() => { if (store.hasModule(STORE_MODULE_NAME)) store.unregisterModule(STORE_MODULE_NAME) })

    const toast = useToast()
    const showError = title => toast({ component: ToastificationContent, props: { title, icon: 'AlertTriangleIcon', variant: 'danger' } })
    const showSuccess = title => toast({ component: ToastificationContent, props: { title, icon: 'CheckIcon', variant: 'success' } })
    const showWarning = title => toast({ component: ToastificationContent, props: { title, icon: 'AlertTriangleIcon', variant: 'warning' } })

    const isLoading = ref(false)
    const game = ref(null)
    const slots = ref([])
    const locked = ref(false)
    const signupUrl = ref(null)
    const adminSignupUrl = ref(null)
    const assigningSlotId = ref(null)
    const isSendingSignupLink = ref(false)
    const isSendingSignupLinksDm = ref(false)
    const isSendingSlotsAdded = ref(false)

    const fetchGame = () => {
      isLoading.value = true
      store.dispatch(`${STORE_MODULE_NAME}/fetchGame`, root.$route.params.id)
        .then(({ data }) => {
          game.value = data.game
          slots.value = data.slots
          locked.value = data.locked
          signupUrl.value = data.signup_url
          adminSignupUrl.value = data.admin_signup_url
        })
        .catch(() => showError('Не удалось загрузить игру'))
        .finally(() => { isLoading.value = false })
    }

    const assignSlot = slot => {
      assigningSlotId.value = slot.id
      store.dispatch(`${STORE_MODULE_NAME}/assignHostSlot`, { gameId: game.value.id, slotId: slot.id })
        .then(({ data }) => {
          if (data.chat_warning) showWarning(data.chat_warning)
          fetchGame()
        })
        .catch(error => {
          const message = error.response?.data?.errors?.slot_id?.[0]
            || error.response?.data?.message || 'Не удалось назначить слот'
          showError(message)
        })
        .finally(() => { assigningSlotId.value = null })
    }

    const copySignupUrl = () => {
      navigator.clipboard.writeText(signupUrl.value)
        .then(() => showSuccess('Ссылка скопирована'))
    }

    const sendSignupLink = () => {
      isSendingSignupLink.value = true
      store.dispatch(`${STORE_MODULE_NAME}/sendSignupLink`, game.value.id)
        .then(() => showSuccess('Ссылка отправлена в чат'))
        .catch(error => showError(error.response?.data?.message || 'Не удалось отправить ссылку в чат'))
        .finally(() => { isSendingSignupLink.value = false })
    }

    // Оповещение в чат: добавились новые слоты, отмечать по той же ссылке
    const sendSlotsAdded = () => {
      isSendingSlotsAdded.value = true
      store.dispatch(`${STORE_MODULE_NAME}/sendSlotsAdded`, game.value.id)
        .then(() => showSuccess('Оповещение о слотах отправлено в чат'))
        .catch(error => showError(error.response?.data?.message || 'Не удалось отправить оповещение в чат'))
        .finally(() => { isSendingSlotsAdded.value = false })
    }

    // Персональные ссылки в ЛС: тост с итогом + предупреждение по каждому неотправленному
    const sendSignupLinksDm = () => {
      isSendingSignupLinksDm.value = true
      store.dispatch(`${STORE_MODULE_NAME}/sendSignupLinksDm`, game.value.id)
        .then(({ data }) => {
          const failed = data.players.filter(player => !player.sent)
          showSuccess(`Отправлено в ЛС: ${data.players.length - failed.length} из ${data.players.length}`)
          failed.forEach(player => showWarning(`${player.name}: ${player.reason}`))
          if (failed.length) {
            // Персональные ссылки неотправленных — админу в буфер, переслать вручную
            navigator.clipboard.writeText(failed.map(player => `${player.name}: ${player.url}`).join('\n'))
              .then(() => showWarning('Ссылки неотправленных скопированы в буфер'))
          }
        })
        .catch(error => showError(error.response?.data?.message || 'Не удалось отправить ссылки в ЛС'))
        .finally(() => { isSendingSignupLinksDm.value = false })
    }

    // Персональная ссылка одному игроку; не отправилось — ссылку в буфер, переслать вручную
    const sendingDmContactId = ref(null)
    const sendSignupLinkDmToPlayer = player => {
      sendingDmContactId.value = player.planfix_contact_id
      store.dispatch(`${STORE_MODULE_NAME}/sendSignupLinkDmToPlayer`, {
        gameId: game.value.id,
        planfixContactId: player.planfix_contact_id,
      })
        .then(({ data }) => {
          const [result] = data.players
          if (result.sent) {
            showSuccess(`Ссылка отправлена в ЛС: ${result.name}`)
            return
          }
          showWarning(`${result.name}: ${result.reason}`)
          navigator.clipboard.writeText(result.url)
            .then(() => showWarning('Персональная ссылка скопирована в буфер'))
        })
        .catch(error => showError(error.response?.data?.message || 'Не удалось отправить ссылку в ЛС'))
        .finally(() => { sendingDmContactId.value = null })
    }

    // «13.09.2026 12:30» по МСК — бэкенд отдаёт даты уже в МСК (toIso8601String() при
    // APP_TIMEZONE=Europe/Moscow), new Date(value) перевёл бы время в часовой пояс браузера,
    // поэтому берём компоненты даты/времени прямо из строки регуляркой (см. FnfLeagueGames.vue)
    const formatMskDateTime = value => {
      const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(value || '')
      if (!match) return '—'
      const [, year, month, day, hours, minutes] = match

      return `${day}.${month}.${year} ${hours}:${minutes}`
    }

    fetchGame()

    return {
      isLoading, game, slots, locked, signupUrl, adminSignupUrl, assigningSlotId, isSendingSignupLink, isSendingSignupLinksDm,
      isSendingSlotsAdded, fetchGame, assignSlot, copySignupUrl, sendSignupLink, sendSignupLinksDm, sendSlotsAdded,
      sendingDmContactId, sendSignupLinkDmToPlayer, formatMskDateTime,
    }
  },
}
</script>
